/*
  We intentionally set variables on the root here so it can be used by other elements
  (like the layout deciding how much padding to add based on the nav width)
*/
:root {
  --sidebar-nav-expanded-width: 272px;
  --sidebar-nav-collapsed-width: 76px;

  --sidebar-nav-width: var(--sidebar-nav-expanded-width);

  &:has(.builder-header:not(.nav-sidebar-position-fixed) .site-sidebar-nav.is-collapsed) {
    --sidebar-nav-width: var(--sidebar-nav-collapsed-width);
  }

  --static-sidebar-nav-width: var(--sidebar-nav-width);

  &:has(.nav-sidebar-position-fixed) {
    --static-sidebar-nav-width: 0px;
  }
}

.site-sidebar-nav {
  min-width: var(--sidebar-nav-width);
  max-width: var(--sidebar-nav-width);
}

.builder-header:not(.nav-sidebar-position-fixed) .is-collapsed .hide-when-sidebar-collapsed {
  display: none;
}

.builder-header.nav-style-left:not(.nav-sidebar-position-fixed) .site-sidebar-nav.is-collapsed {
  display: var(--sidebar-nav-collapsed-display-when-not-fixed, block);
}

.site-sidebar-nav {
  @apply absolute top-full z-10 bg-bg-alt overflow-auto flex px-4 py-6 flex-col;
  /* 100dvh avoids Android/Samsung address-bar jumps; 100vh kept as fallback. */
  bottom: calc(-100vh + 100%);
  bottom: calc(-100dvh + 100%);

  &.site-sidebar-nav-position-left {
    @apply left-0;
  }

  &.site-sidebar-nav-position-right {
    @apply right-0;
  }

  &:is(.nav-sidebar-position-fixed *) {
    @apply transition-transform duration-150;
    /* Prevent off-canvas transformed panel from expanding document scroll width
       (common horizontal-scroll cause on Samsung Internet / Android WebViews). */
    max-width: min(var(--sidebar-nav-width), 100vw);
    will-change: transform;

    &.site-sidebar-nav-position-left {
      @apply -translate-x-full;
      left: 0;
    }

    &.site-sidebar-nav-position-right {
      @apply translate-x-full;
      right: 0;
    }

    &.is-open {
      @apply translate-x-0 shadow-lg;
    }

    &:not(.is-open):not(.is-animating) {
      @apply hidden;
      /* Extra belt-and-suspenders: never contribute to scroll while closed. */
      visibility: hidden;
      pointer-events: none;
    }
  }
}

/* Keyboard nav focus highlight inside course search */
[data-search-text].is-nav-focused > a {
  @apply bg-bg-alt rounded;
}

.divider-after-main-nav-list {

  /*
   Some what hacky, but a nice improvement:
    - We render a divider after the main nav list.
    - There can be a bunch of optional things after the main nav list: system links like favorites, a CTA button, social links.
    - Things like system links are rendered by this component, but the other things are just HTML slots.
    - We do not know the structure of the all of the optional things, but sometimes they have are only visible on mobile (have the lg:hidden class)
    - So we do a small little thing where, if all the optional things have .lg:hidden class, hide the divider on `lg` screen because otherwise we'd show a ghost divider.
  */
  @media screen(lg) {
    &:not(:has(~ *:not(.lg\:hidden))) {
      display: none !important;
    }
  }
}

/*
  Colored folder panels.

  The folder's own hex becomes the --panel-*-light / --panel-*-dark custom properties
  NavPanelPalette sets inline, which it also aliases onto the header's own theme
  variables — so every existing nav rule repaints without an override.

  Everything Tailwind can express is @apply'd or a utility on the element. What is
  left is the per-record color plumbing: a customer-picked hex reaches CSS only as a
  custom property, and `bg-[var(--x)]`-style bracket classes are not allowed here.
*/
.site-nav-panel {
  --panel-bg: var(--panel-bg-light);
  --panel-bg-end: var(--panel-bg-end-light);
  --panel-heading: var(--panel-heading-light);
  --panel-text: var(--panel-text-light);
  --panel-nested-bg: var(--panel-nested-bg-light);
  --panel-active-bg: var(--panel-active-bg-light);
  --panel-active-text: var(--panel-active-text-light);
  --panel-rail: var(--panel-rail-light);

  @apply rounded-xl p-2 my-1.5;
  background-image: linear-gradient(to bottom, var(--panel-bg), var(--panel-bg-end));

  &:is(.dark *) {
    --panel-bg: var(--panel-bg-dark);
    --panel-bg-end: var(--panel-bg-end-dark);
    --panel-heading: var(--panel-heading-dark);
    --panel-text: var(--panel-text-dark);
    --panel-nested-bg: var(--panel-nested-bg-dark);
    --panel-active-bg: var(--panel-active-bg-dark);
    --panel-active-text: var(--panel-active-text-dark);
    --panel-rail: var(--panel-rail-dark);
  }
}

/*
  The collapsed rail is exactly one icon row wide, so the panel's own side padding
  would push each row past its edge and clip the active pill. Same guard the
  hide-when-sidebar-collapsed rule above uses: the fixed drawer is never narrow.
*/
.builder-header:not(.nav-sidebar-position-fixed) .is-collapsed .site-nav-panel {
  @apply px-0;
}

/*
  For the same reason, a classic scrollbar (Windows, or macOS with "always show
  scrollbars") takes ~15px of that one row when a long nav overflows, squeezing
  every icon and clipping it. Hide it on the collapsed rail only; wheel, touch and
  keyboard still scroll it.
*/
.builder-header:not(.nav-sidebar-position-fixed) .site-sidebar-nav.is-collapsed {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.site-nav-panel-heading {
  @apply px-3 pt-1 pb-2 font-bold uppercase leading-none text-2xs tracking-widest;
  color: var(--panel-heading);
}

/*
  Two fills the header theme has no variable for. Both need !important: the theme
  paints them from `.builder-header.builder-header ...`, which outranks any selector
  available from this file.
*/
.site-nav-panel-folder-row {
  background-color: var(--panel-nested-bg) !important;
}

/* The iconless-link fallback chip borrows the link color as a FILL, which inside a
   panel would leave an inert chip darker than the active row. Reuse the nested-row
   pair instead — NavPanelPaletteTest already asserts it clears AA. */
.site-nav-panel .nav-list-link-letter-icon {
  background-color: var(--panel-nested-bg) !important;
  color: var(--panel-text) !important;
}
